<template>
	<div ref="root" class="template-select-control" @focusout="handleFocusOut">
		<button
			ref="trigger"
			type="button"
			role="combobox"
			aria-label="Select template"
			aria-haspopup="listbox"
			:aria-expanded="isOpen"
			:aria-controls="listId"
			:aria-activedescendant="isOpen ? `${listId}-${activeIndex}` : undefined"
			:disabled="disabled"
			class="template-select-trigger"
			:class="{ 'is-open': isOpen, 'is-placeholder': !modelValue }"
			@click="isOpen ? close() : open()"
			@keydown="handleKeydown"
		>
			<span class="template-select-label">{{ disabled ? 'Loading templates…' : selectedLabel }}</span>
			<AppointmentIcon name="chevron-down" class="template-select-chevron" />
		</button>
		<Transition name="template-menu">
			<div v-if="isOpen" class="template-select-popup">
				<ul :id="listId" role="listbox" aria-label="Templates" class="template-select-options">
					<li
						v-for="(option, index) in choices"
						:id="`${listId}-${index}`"
						:key="option.value"
						role="option"
						:aria-selected="option.value === String(modelValue)"
						class="template-select-option"
						:class="{ 'is-highlighted': index === activeIndex, 'is-selected': option.value === String(modelValue) }"
						@mouseenter="activeIndex = index"
						@mousedown.prevent
						@click="select(index)"
					>
						<span class="template-option-label">{{ option.label }}</span>
						<AppointmentIcon v-if="option.value === String(modelValue)" name="check" :size="16" />
					</li>
				</ul>
				<p v-if="!options.length" class="template-select-empty">No templates yet.</p>
			</div>
		</Transition>
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onDeactivated, onMounted, ref, watch } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';

const props = defineProps({
	modelValue: { type: [String, Number], default: '' },
	options: { type: Array, default: () => [] },
	disabled: Boolean,
});
const emit = defineEmits(['update:modelValue']);
const listId = `template-options-${getCurrentInstance().uid}`;
const root = ref(null);
const trigger = ref(null);
const isOpen = ref(false);
const activeIndex = ref(0);
let search = '';
let searchTimer;
let pointerInteraction = false;
const choices = computed(() => [
	{ value: '', label: 'Select template' },
	...props.options.map(option => ({ value: String(option.id), label: option.name || 'Untitled template' })),
]);
const selectedIndex = computed(() => Math.max(0, choices.value.findIndex(option => option.value === String(props.modelValue))));
const selectedLabel = computed(() => choices.value[selectedIndex.value].label);

const scrollToActive = () => nextTick(() => {
	root.value?.querySelector(`[id="${listId}-${activeIndex.value}"]`)?.scrollIntoView?.({ block: 'nearest' });
});
const open = () => {
	if (props.disabled) return;
	activeIndex.value = selectedIndex.value;
	isOpen.value = true;
	scrollToActive();
};
const close = () => {
	pointerInteraction = false;
	isOpen.value = false;
	search = '';
	clearTimeout(searchTimer);
};
const select = index => {
	if (props.disabled || !choices.value[index]) return;
	emit('update:modelValue', choices.value[index].value);
	close();
	trigger.value?.focus();
};
const handleKeydown = event => {
	pointerInteraction = false;
	if (props.disabled) return;
	if (event.key === 'Escape' && isOpen.value) {
		event.preventDefault();
		event.stopPropagation(); // First Escape closes this list, not the Templates dialog.
		close();
		return;
	}
	if (event.key === 'Tab') {
		close();
		return;
	}
	if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
		event.preventDefault();
		if (!isOpen.value) {
			open();
			if (event.key === 'Home') activeIndex.value = 0;
			if (event.key === 'End') activeIndex.value = choices.value.length - 1;
		} else if (event.key === 'Enter' || event.key === ' ') {
			select(activeIndex.value);
		} else if (event.key === 'Home') activeIndex.value = 0;
		else if (event.key === 'End') activeIndex.value = choices.value.length - 1;
		else activeIndex.value = Math.max(0, Math.min(choices.value.length - 1, activeIndex.value + (event.key === 'ArrowDown' ? 1 : -1)));
		scrollToActive();
		return;
	}
	if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
		event.preventDefault();
		if (!isOpen.value) open();
		search += event.key.toLocaleLowerCase();
		clearTimeout(searchTimer);
		searchTimer = setTimeout(() => { search = ''; }, 600);
		const index = choices.value.findIndex(option => option.label.toLocaleLowerCase().startsWith(search));
		if (index >= 0) {
			activeIndex.value = index;
			scrollToActive();
		}
	}
};
const handleOutside = event => {
	pointerInteraction = false;
	if (!root.value?.contains(event.target)) close();
};
const handlePointerDown = () => { pointerInteraction = true; };
const handleFocusOut = event => {
	// Collapsing this in-flow list before click moves the button under the finger.
	if (pointerInteraction) return;
	if (!root.value?.contains(event.relatedTarget)) close();
};
watch(() => props.disabled, disabled => { if (disabled) close(); });
watch(choices, () => { activeIndex.value = selectedIndex.value; });
onMounted(() => {
	document.addEventListener('pointerdown', handlePointerDown, true);
	document.addEventListener('click', handleOutside, true);
});
onDeactivated(close);
onBeforeUnmount(() => {
	document.removeEventListener('pointerdown', handlePointerDown, true);
	document.removeEventListener('click', handleOutside, true);
	clearTimeout(searchTimer);
});
</script>

<style scoped>
.template-select-control { position: relative; min-width: 0; }
.template-select-trigger {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	width: 100%;
	height: 36px;
	padding: 0 12px;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	color: var(--text-color, #414552);
	font: var(--ap-type-body);
	text-align: left;
	cursor: pointer;
	transition: background-color 150ms ease, border-color 150ms ease;
}
.template-select-trigger:hover:not(:disabled) { background: var(--noc-grey-light, #f9f9f9); }
.template-select-trigger.is-open { border-color: var(--noc-blue, #0a61c9); }
.template-select-trigger.is-placeholder { color: var(--text-color-secondary, #5e6170); }
.template-select-trigger:disabled { opacity: 0.6; cursor: not-allowed; }
.template-select-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.template-select-chevron { color: var(--text-color-secondary); transition: transform 150ms ease; }
.is-open .template-select-chevron { transform: rotate(180deg); }
.template-select-popup {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	right: 0;
	z-index: 10;
	padding: 4px;
	border: 1px solid var(--border-color, #e3e6eb);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	box-shadow: 0 8px 24px rgba(25, 30, 45, 0.1);
}
.template-select-options { list-style: none; margin: 0; padding: 0; max-height: min(240px, 40vh); overflow-y: auto; }
.template-select-option {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	min-height: 34px;
	padding: 6px 8px;
	border-radius: 5px;
	color: var(--text-color, #414552);
	font: var(--ap-type-body);
	cursor: pointer;
}
.template-option-label { overflow-wrap: anywhere; }
.template-select-option.is-highlighted { background: var(--noc-grey-light, #f9f9f9); }
.template-select-option.is-selected { color: var(--noc-blue, #0a61c9); background: var(--noc-blue-05); }
.template-select-empty { margin: 0; padding: 10px 8px; color: var(--text-color-secondary); font: var(--ap-type-caption); }
.template-menu-enter-active, .template-menu-leave-active { transition: opacity 120ms ease, transform 120ms ease; }
.template-menu-enter-from, .template-menu-leave-to { opacity: 0; transform: translateY(-3px); }
@media (prefers-reduced-motion: reduce) {
	.template-menu-enter-active, .template-menu-leave-active, .template-select-chevron { transition: none; }
}
</style>
